/* Shared machinery for the mission charts: a viewport both of them read, a
 * range derived from the data rather than assumed, and a time axis that picks
 * human intervals.
 *
 * This exists because the two charts on the Sound tab are one picture. They
 * share a time axis, a hover position and a zoom level, and the moment any of
 * those lives inside one of them the other is guessing.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { clock, currentZone, dayLabel, stampFull } from './time'

export interface View {
  /** Epoch seconds, inclusive. */
  t0: number
  t1: number
}

/* ------------------------------------------------------------------ range */

/** Value at a quantile of an already-sorted array. */
export function quantile(sorted: number[], q: number): number {
  if (!sorted.length) return 0
  const i = (sorted.length - 1) * q
  const lo = Math.floor(i)
  const hi = Math.ceil(i)
  if (lo === hi) return sorted[lo]!
  return sorted[lo]! + (sorted[hi]! - sorted[lo]!) * (i - lo)
}

/** A display range taken from the data, not from an assumption about it.
 *
 *  The old chart hard-coded -140..-80 dBFS and the one before it -95..-20. Both
 *  were reasonable guesses about a hydrophone and both were wrong for this one:
 *  measured over six hours, the median band level here is -140.8, so half of
 *  every sample sat exactly on the floor and 90% of the mid bands were clamped
 *  to the darkest colour. What survived was a handful of transients, and the
 *  operator reasonably reported that the spectrum had disappeared.
 *
 *  Quantiles rather than min/max because a single dropout at -200 would
 *  otherwise flatten everything real into the top half of the scale.
 *
 *  Deliberately computed once over the whole loaded window and not per zoom
 *  level: a colour that means something different after you pan is worse than
 *  one that is merely badly chosen, because nothing on screen says it changed.
 */
export function autoRange(
  values: number[], loQ = 0.005, hiQ = 0.9995, minSpan = 24,
): [number, number] {
  const clean = values.filter((v) => Number.isFinite(v) && v > -190).sort((a, b) => a - b)
  if (clean.length < 2) return [-140, -80]

  let lo = quantile(clean, loQ)
  let hi = quantile(clean, hiQ)

  // Headroom, so the loudest thing in the window is not painted at the very
  // top of the scale where it cannot be told from something louder still.
  const pad = Math.max(2, (hi - lo) * 0.06)
  lo -= pad
  hi += pad

  // A window with nothing happening in it would otherwise get a 3 dB range and
  // paint ordinary noise as though it were structure.
  if (hi - lo < minSpan) {
    const mid = (hi + lo) / 2
    lo = mid - minSpan / 2
    hi = mid + minSpan / 2
  }

  // Round outward to 5 dB, so the legend reads as a decision rather than as an
  // artefact of whatever happened to be in the buffer.
  return [Math.floor(lo / 5) * 5, Math.ceil(hi / 5) * 5]
}

/* ------------------------------------------------------------- time ticks */

const STEPS = [
  1, 2, 5, 10, 15, 30,                       // seconds
  60, 120, 300, 600, 900, 1800,              // minutes
  3600, 7200, 10800, 21600, 43200,           // hours
  86400, 172800, 604800,                     // days
]

/** Tick marks at intervals a person would choose, aligned to the clock.
 *
 *  Five evenly spaced labels across whatever the span happens to be gives
 *  "14:37, 16:52, 19:08" -- technically correct and unreadable. Aligning to the
 *  step means the labels land on the hour, and moving the window slides the
 *  labels instead of renaming them.
 */
export function timeTicks(t0: number, t1: number, target = 6): number[] {
  const span = Math.max(1, t1 - t0)
  const ideal = span / target
  const step = STEPS.find((s) => s >= ideal) ?? STEPS[STEPS.length - 1]!
  // Aligned to the clock the viewer has chosen, so a day boundary is midnight
  // where they are standing -- or exactly at UTC, if that is what they asked
  // for. Reading the offset from the instant rather than from `new Date()`
  // matters twice a year: the offset in force in January is not the one in
  // force across a window in July.
  const offset = currentZone() === 'utc'
    ? 0
    : -new Date(t0 * 1000).getTimezoneOffset() * 60
  const first = Math.ceil((t0 + offset) / step) * step - offset
  const out: number[] = []
  for (let t = first; t <= t1; t += step) out.push(t)
  return out
}

/** Label for a tick, given how much time is on screen. */
export function tickLabel(t: number, span: number): string {
  if (span > 5 * 86400) return dayLabel(t)
  if (span > 36 * 3600) return `${dayLabel(t)} ${clock(t)}`
  return clock(t, span < 900)
}

/** Full timestamp for the readout, where there is room to be unambiguous --
 *  which means naming the zone, because a bare `03:14` is not. */
export function stamp(t: number): string {
  return stampFull(t)
}

export function fmtSpan(s: number): string {
  if (s < 90) return `${Math.round(s)} s`
  if (s < 5400) return `${Math.round(s / 60)} min`
  if (s < 36 * 3600) return `${(s / 3600).toFixed(1)} h`
  return `${(s / 86400).toFixed(1)} days`
}

/* --------------------------------------------------------------- viewport */

const MIN_SPAN_S = 30
const EASE_MS = 420

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)

export interface Viewport {
  view: View
  /** True while an eased transition is running, for suppressing hover. */
  animating: boolean
  zoomed: boolean
  setView: (v: View, animate?: boolean) => void
  zoomAt: (factor: number, atFraction: number) => void
  panByFraction: (f: number) => void
  reset: () => void
}

/** The shared window, with easing where easing helps and none where it hurts.
 *
 *  Direct manipulation -- dragging, the wheel, a pinch -- is applied on the
 *  frame it arrives. Anything else -- reset, a range button, clicking an event
 *  to jump to it -- is eased, because the picture changing under you with no
 *  motion between the two states is the thing that makes a chart feel like it
 *  reloaded rather than moved.
 */
export function useViewport(full: View): Viewport {
  const [view, setViewState] = useState<View>(full)
  const [animating, setAnimating] = useState(false)
  const raf = useRef(0)
  const fullRef = useRef(full)
  fullRef.current = full

  const calm = useRef(false)
  useEffect(() => {
    calm.current = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
  }, [])

  // New data, or a new time range: start from the whole of it. Compared by
  // value because the caller rebuilds this object on every render.
  useEffect(() => {
    cancelAnimationFrame(raf.current)
    setAnimating(false)
    setViewState(full)
  }, [full.t0, full.t1])

  const clamp = useCallback((v: View): View => {
    const f = fullRef.current
    const fullSpan = Math.max(1, f.t1 - f.t0)
    let span = Math.min(Math.max(v.t1 - v.t0, MIN_SPAN_S), fullSpan)
    let t0 = v.t0
    // Slide back inside rather than refusing to move: a drag that runs off the
    // end should stop at the end, not stick where it was.
    if (t0 < f.t0) t0 = f.t0
    if (t0 + span > f.t1) t0 = f.t1 - span
    return { t0, t1: t0 + span }
  }, [])

  const setView = useCallback((next: View, animate = false) => {
    const to = clamp(next)
    cancelAnimationFrame(raf.current)
    if (!animate || calm.current) {
      setAnimating(false)
      setViewState(to)
      return
    }
    setAnimating(true)
    let from: View | null = null
    let start = 0
    const step = (now: number) => {
      if (!start) { start = now }
      const k = easeOutCubic(Math.min(1, (now - start) / EASE_MS))
      setViewState((cur) => {
        if (!from) from = cur
        return {
          t0: from.t0 + (to.t0 - from.t0) * k,
          t1: from.t1 + (to.t1 - from.t1) * k,
        }
      })
      if (k < 1) raf.current = requestAnimationFrame(step)
      else setAnimating(false)
    }
    raf.current = requestAnimationFrame(step)
  }, [clamp])

  useEffect(() => () => cancelAnimationFrame(raf.current), [])

  /** Zoom about a point, so the sample under the cursor stays under it. */
  const zoomAt = useCallback((factor: number, at: number) => {
    setViewState((cur) => {
      const span = cur.t1 - cur.t0
      const anchor = cur.t0 + span * at
      const next = span * factor
      return clamp({ t0: anchor - next * at, t1: anchor - next * at + next })
    })
  }, [clamp])

  const panByFraction = useCallback((f: number) => {
    setViewState((cur) => {
      const span = cur.t1 - cur.t0
      return clamp({ t0: cur.t0 + span * f, t1: cur.t1 + span * f })
    })
  }, [clamp])

  const reset = useCallback(() => setView(fullRef.current, true), [setView])

  const fullSpan = full.t1 - full.t0
  return {
    view, animating, reset, setView, zoomAt, panByFraction,
    // Tolerance, not equality: an eased reset lands a hair short of the edge
    // and a Reset button that stays lit afterwards reads as broken.
    zoomed: fullSpan > 0 && (view.t1 - view.t0) < fullSpan * 0.999,
  }
}

/* -------------------------------------------------------------- selection */

/** The grid a selection snaps to, in seconds.
 *
 *  One segment. Audio is written in 60 s files and the node's inventory is
 *  hourly, so nothing behind this chart can answer a question posed to the
 *  second: a selection from 14:03:17 to 14:11:42 would be served by fetching
 *  14:03:00 to 14:12:00 and would have promised a precision that does not
 *  exist. Snapping makes the request and the answer the same shape, and lets
 *  the readout say "9 segments" rather than "8 min 25 s" -- which is the unit
 *  the thing is actually stored, priced and transferred in.
 */
export const SNAP_S = 60

export interface Selection {
  t0: number
  t1: number
}

export interface Selector {
  /** Committed, snapped, and what every action reads. */
  sel: Selection | null
  /** Mid-drag, unsnapped, for painting only. */
  live: Selection | null
  mode: 'pan' | 'select'
  setMode: (m: 'pan' | 'select') => void
  begin: (t: number) => void
  extend: (t: number) => void
  commit: () => void
  clear: () => void
  /** Move one edge by whole snap steps: the keyboard path to the same result. */
  nudge: (edge: 't0' | 't1', steps: number) => void
}

function snap(s: Selection): Selection {
  // Outward on both edges, so a selection never asks for less than what was
  // dragged over. Rounding inward could silently drop the segment someone was
  // pointing at, which is the only segment they certainly wanted.
  const t0 = Math.floor(Math.min(s.t0, s.t1) / SNAP_S) * SNAP_S
  const t1 = Math.ceil(Math.max(s.t0, s.t1) / SNAP_S) * SNAP_S
  return { t0, t1: Math.max(t1, t0 + SNAP_S) }
}

/** A brushed time range, snapped to the segment grid.
 *
 *  Kept apart from `useViewport` on purpose. The viewport is where you are
 *  looking; a selection is what you are asking about. Zooming out to see
 *  context around a selection must not resize or drop it, and the two would be
 *  impossible to keep separate if one owned the other.
 */
export function useSelection(): Selector {
  const [sel, setSel] = useState<Selection | null>(null)
  const [live, setLive] = useState<Selection | null>(null)
  const [mode, setMode] = useState<'pan' | 'select'>('pan')
  const anchor = useRef(0)

  const begin = useCallback((t: number) => {
    anchor.current = t
    setLive({ t0: t, t1: t })
  }, [])

  const extend = useCallback((t: number) => {
    setLive({ t0: anchor.current, t1: t })
  }, [])

  const commit = useCallback(() => {
    setLive((cur) => {
      if (cur) {
        // A click that never moved is a click, not a one-segment selection.
        // Treating it as a selection would make every attempt to dismiss one
        // create another.
        if (Math.abs(cur.t1 - cur.t0) < 1) setSel(null)
        else setSel(snap(cur))
      }
      return null
    })
  }, [])

  const clear = useCallback(() => { setSel(null); setLive(null) }, [])

  const nudge = useCallback((edge: 't0' | 't1', steps: number) => {
    setSel((cur) => {
      if (!cur) return cur
      const next = { ...cur, [edge]: cur[edge] + steps * SNAP_S }
      // Through-and-past rather than blocked: dragging the left edge past the
      // right should invert the selection, which is what the pointer path
      // already does via snap().
      return snap(next)
    })
  }, [])

  return { sel, live, mode, setMode, begin, extend, commit, clear, nudge }
}

/* ------------------------------------------------------------ canvas sizing */

export const DPR = () => Math.max(1, Math.min(2, window.devicePixelRatio || 1))

/** Size a canvas to its box in device pixels and hand back a scaled context. */
export function fit(canvas: HTMLCanvasElement, height: number) {
  const dpr = DPR()
  const rect = canvas.getBoundingClientRect()
  const w = Math.max(64, Math.round(rect.width))
  canvas.width = Math.round(w * dpr)
  canvas.height = Math.round(height * dpr)
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  return { ctx, w, h: height }
}
